/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import * as Yup from 'yup';
import {
  AccordionSection,
  Banner,
  Button,
  ButtonExportCSVAsync,
  ButtonText,
  Card,
  CardAction,
  CardContent,
  CardDetail,
  CardIcon,
  CardIconStatus,
  CardLabel,
  CardRow,
  CardValue,
  DateRangePicker,
  Empty,
  FieldWrapper,
  FilterButton,
  FilterNested,
  H1,
  H4,
  HeaderCell,
  HeaderRow,
  IconTypes,
  Input,
  PageActions,
  PageHeader,
  PageHeaderAction,
  PageHeaderAvatar,
  PageHeaderAvatarLayout,
  PageHeaderContent,
  Select,
  Table,
  TableBody,
  TableHead,
  TooltipIcon,
  TooltipWrapper,
} from 'components';
import { ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { DDOption, DisbursementOptions } from 'config/const';
import { Appearance } from 'design/types';
import validations from 'lib/Validations';
import { ActivateUser, AddUserToSite, EditSite, EditUser } from 'modals';
import { routeConstants } from 'routes';
import { PermissionGate } from 'screens/Security';
import { PaymentMethodAccountNumber } from 'screens/Settings/components';
import ReviewUserScreen from 'screens/Users/ReviewUser';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentDateRange } from 'store/App';
import { getEmployeePayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections/Payouts';
import {
  addEmployeeToSiteRequest,
  Employee,
  getEmployeeCardRequest,
  getEmployeeRequest,
  removeEmployeeFromSiteRequest,
  resendInvitationRequest,
  selectEmployee,
  updateEmployeeRequest,
  updateEmployeeSiteRoleRequest,
  updateOrgEmployeeTypeRequest,
} from 'store/Employee';
import { selectCurrentEmployeeAdminSites, selectCurrentEmployeeType, selectIsPermitted } from 'store/Employers';
import { getEmployeePayoutStatsRequest, selectPayoutStatsByStatus, Stats } from 'store/Stats';
import { TipCalculator, unlinkEmployeeRequest } from 'store/TipCalculator';
import { IJobTitleState, IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import {
  ApiQueryOperator,
  EmployeeActionRequiredType,
  EmployeeRole,
  EmployeeStatus,
  EmployeeType,
  IApiErrorPayloadWithCode,
  IApiQueryParam,
  IEmployee,
  IEmployeeIntegration,
  IEmployeeSite,
  IPayoutStateByStatus,
  IUpdateEmployeeSite,
  PayoutStatus,
  RequestStatus,
  SiteRole,
} from 'types';
import { capitalize, centsToDollarsDisplay, formatDateForUI, formatJobTitle, formatMobile, getAvailableEmployerSites, getPaymentTypeIcon, isSiteRole, sortAlphaNumerically } from 'utils';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { roleSiteDisplay } from 'utils/employee';
import * as API from '../../api';
import { CenteredButtonAdd, EmptyContainer, PersonalInfoLayout, RealName, ShorterEmptyContainer, SiteRow, SiteRowAdmin, StyledSection, TableContainer, UserNames } from './components';
import { UserDetailPayouts } from './UserDetailPayouts';

interface IEditUserSiteRole {
  site: DDOption;
  jobTitle?: ICreatableSelectOption<IJobTitleState>;
  role?: DDOption;
  disbursementMethod?: DDOption;
  employeeId?: string;
}

const paymentHistoryStatusOptions = [
  { label: 'All', value: null },
  { label: 'Paid', value: PayoutStatus.PAID },
  { label: 'Payroll', value: PayoutStatus.PAYROLL },
  { label: 'Cash', value: PayoutStatus.CASH },
  { label: 'Pending', value: PayoutStatus.PENDING },
  { label: 'Expired', value: PayoutStatus.EXPIRED },
  { label: 'Canceled', value: PayoutStatus.CANCELED },
];

const UserDetail: React.FC = () => {
  const [addSite, setAddSite] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<boolean>(false);
  const [reactivateUser, setReactivateUser] = useState<boolean>(false);
  const [paymentFilter, setPaymentFilters] = useState<boolean>(false);
  const [reviewEmployeeModalVisible, setReviewEmployeeModalVisible] = useState<boolean>(false);
  const currentDateRange = useSelector(selectCurrentDateRange());

  const [currentEditSite, setCurrentEditSite] = useState<IEmployeeSite | null>(null);

  // payment history state
  // --------------------
  const defaultRecordsPerPage = 10;
  const defaultOffset = 0;
  const [paymentHistoryRecordsPerPage, setPaymentHistoryRecordsPerPage] = useState<number>(defaultRecordsPerPage);
  const [paymentHistoryOffset, setPaymentHistoryOffset] = useState<number>(defaultOffset);
  const [paymentHistorySort, setPaymentHistorySort] = useState<string>('-payment_date');
  const [paymentHistoryStatusFilter, setPaymentHistoryStatusFilter] = useState<string>('');
  const [paymentHistorySiteFilter, setPaymentHistorySiteFilter] = useState<string>('');

  const q = useMemo(
    () => [
      { key: 'payment_date', operator: ApiQueryOperator.gte, value: currentDateRange.startDate },
      { key: 'payment_date', operator: ApiQueryOperator.lte, value: currentDateRange.endDate },
      ...(paymentHistoryStatusFilter ? [{ key: 'status', operator: ApiQueryOperator.in, value: paymentHistoryStatusFilter }] : []),
      ...(paymentHistorySiteFilter ? [{ key: 'site_id', operator: ApiQueryOperator.eq, value: paymentHistorySiteFilter }] : []),
    ],
    [currentDateRange, paymentHistoryStatusFilter, paymentHistorySiteFilter]
  );

  const qWithoutDates = q.filter((item) => item.key !== 'payment_date');

  // -------------------

  const { id } = useParams() as any;
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector(selectCurrentUser());
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const employeeDetail = useSelector(selectEmployee());
  const fullName = employeeDetail?.name || '';
  const sites = useSelector(selectCurrentEmployeeAdminSites);
  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());
  const payoutStats = useSelector(selectPayoutStatsByStatus());
  const isPayoutsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_EMPLOYEE_PAYOUTS_REQUEST));
  const isPayoutStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_EMPLOYEE_PAYOUT_STATS_REQUEST));
  const isFetchEmployeeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.GET_EMPLOYEE_REQUEST));
  const isUpdateSiteRoleLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));
  const isAddEmployeeToSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
  const isRemoveFromSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.REMOVE_EMPLOYEE_FROM_SITE_REQUEST));
  const isUpdateEmployeeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_REQUEST));
  const isUnlinkingEmployeeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, TipCalculator.UNLINK_EMPLOYEE_REQUEST));
  const isGetEmployeeDetailCardLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.GET_EMPLOYEE_CARD_REQUEST));

  const getEmployeeStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.GET_EMPLOYEE_REQUEST));
  const updateEmployeeStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.UPDATE_EMPLOYEE_REQUEST));
  const addSiteStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));

  const resendInvitation = () => {
    dispatch(resendInvitationRequest(employeeDetail?.id, employeeDetail?.inviteRecipient || ''));
  };

  const disableStatusAction = !useSelector(selectIsPermitted([EmployeeRole.SITE_ADMIN, EmployeeRole.ADMIN]));
  const hasEditPermission = (employeeDetail && employeeDetail.type !== EmployeeType.ADMIN) || currentEmployeeType === EmployeeType.ADMIN;

  const statusDisplay: {
    color: Appearance;
    icon: IconTypes;
    text: string;
    actionText?: string;
    action?: () => void;
    disabled?: boolean;
    hideAction?: boolean;
  } | null = (() => {
    if (isEmpty(employeeDetail?.sites)) {
      return {
        color: Appearance.WARNING,
        icon: 'Caution' as IconTypes,
        text: 'Inactive',
      };
    }

    switch (employeeDetail?.status) {
      case EmployeeStatus.ACTION_REQUIRED:
        if (employeeDetail.actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
          return {
            color: Appearance.WARNING,
            icon: 'Caution' as IconTypes,
            text: 'Review required',
            actionText: 'Review Now',
            action: () => showReviewModal(),
            disabled: disableStatusAction || !hasEditPermission,
          };
        }
        if (employeeDetail.actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED) {
          return {
            color: Appearance.DANGER,
            icon: 'RemoveThick' as IconTypes,
            text: 'Expired',
            actionText: `${employeeDetail?.sites?.length ? 'Resend Invitation' : ''}`,
            action: () => resendInvitation(),
            hideAction: disableStatusAction || !hasEditPermission,
          };
        }
        return null;

      case EmployeeStatus.DISMISSED:
        return {
          color: Appearance.DANGER,
          icon: 'RemoveThick' as IconTypes,
          text: 'Dismissed',
        };

      case EmployeeStatus.INVITED:
        return {
          color: Appearance.WARNING,
          icon: 'Caution' as IconTypes,
          text: 'Pending',
          actionText: `${employeeDetail?.sites?.length ? 'Resend Invitation' : ''}`,
          action: () => resendInvitation(),
          hideAction: disableStatusAction || !hasEditPermission,
        };

      case EmployeeStatus.APPROVED:
        return {
          color: Appearance.SUCCESS,
          icon: 'CheckmarkThick' as IconTypes,
          text: 'No Issues',
        };

      default:
        return null;
    }
  })();

  const { pending, expired } = useMemo(
    () =>
      (payoutStats || []).reduce<Partial<Record<PayoutStatus, IPayoutStateByStatus>>>((acc, stat) => {
        acc[stat.status] = stat;
        return acc;
      }, {}),
    [payoutStats]
  );

  const getEmployeePayoutStats = useCallback(() => dispatch(getEmployeePayoutStatsRequest(id)), [id]);

  const updatePayouts = useCallback(() => {
    if (pending?.count > 0 || expired?.count > 0) {
      dispatch(getEmployeePayoutsRequest(id, { sort: paymentHistorySort, limit: paymentHistoryRecordsPerPage, offset: paymentHistoryOffset, q }));
      getEmployeePayoutStats();
    }
  }, [id, pending, expired, paymentHistoryOffset, paymentHistoryRecordsPerPage, paymentHistorySort, q, getEmployeePayoutStats]);

  useEffect(() => {
    if (id) {
      dispatch(getEmployeeRequest(id));
      getEmployeePayoutStats();
    }
  }, [id]);

  useEffect(() => {
    if (getEmployeeStatus === RequestStatus.FAILED) history.push(routeConstants.private.employees);
  }, [getEmployeeStatus]);

  const validationSchema = Yup.object().shape({
    ...validations.employeeSiteValidationSchema(sites),
  });

  const submitAddToSite = (values: { role: DDOption; jobTitle: ICreatableSelectOption<IJobTitleState>; employeeId: string; site: DDOption; disbursementMethod: DDOption }) => {
    const payload = {
      id: values.site.value,
      role: values.role?.value === EmployeeRole.SITE_ADMIN ? EmployeeRole.ADMIN : values.role?.value,
      job: { title: values.jobTitle?.value?.title },
      disbursementMethod: values.disbursementMethod.value,
      ...(!isEmpty(values.employeeId) ? { employeeId: values.employeeId } : {}),
    };
    dispatch(addEmployeeToSiteRequest(employeeDetail?.id, payload));
  };

  const AddUserFormik = useFormik({
    initialValues: {
      role: { label: '', value: '' },
      jobTitle: {
        label: '',
        value: {
          title: '',
        } as IJobTitleState,
      },
      site: { label: '', value: '' },
      disbursementMethod: { label: '', value: '' },
      employeeId: '',
    },
    onSubmit: (values: { role: DDOption; jobTitle: ICreatableSelectOption<IJobTitleState>; employeeId: string; site: DDOption; disbursementMethod: DDOption }) => {
      // add employee to a site
      if (values.role.value === EmployeeRole.ADMIN) {
        dispatch(updateOrgEmployeeTypeRequest(employeeDetail.id));
      } else {
        submitAddToSite(values);
      }
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (addSiteStatus === RequestStatus.SUCCESS) {
      setAddSite(false);
      AddUserFormik.resetForm();
      dispatch(clearRequest(Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
    }
  }, [addSiteStatus, setAddSite, dispatch, AddUserFormik]);

  useEffect(() => {
    if (updateEmployeeStatus === RequestStatus.SUCCESS) {
      AddUserFormik.resetForm();
      setEditUser(false);
      setReactivateUser(false);
      setAddSite(false);
      setCurrentEditSite(null);
      dispatch(clearRequest(Employee.UPDATE_EMPLOYEE_REQUEST));
      updatePayouts();
    }
  }, [updateEmployeeStatus]);

  const sortedManagedsites = employeeDetail?.sites && [...employeeDetail.sites].sort(sortAlphaNumerically('name'));

  const nonAdminSiteTable = (
    <Table appearance="noContainer">
      <TableHead>
        <HeaderRow bgColor="transparent">
          <HeaderCell align="left">Site</HeaderCell>
          <HeaderCell align="left">Role</HeaderCell>
          <HeaderCell align="left">Job Title</HeaderCell>
          <HeaderCell align="left">Employee ID</HeaderCell>
          <HeaderCell align="left">Disbursement</HeaderCell>
          <HeaderCell align="left">Status</HeaderCell>
          <HeaderCell align="right" />
        </HeaderRow>
      </TableHead>

      <TableBody>
        {employeeDetail && sortedManagedsites
          ? sortedManagedsites.map((site, key) => (
              <SiteRow
                key={key}
                userType={employeeDetail.type}
                role={roleSiteDisplay(site.role)}
                site={site.name}
                jobTitle={site.job && formatJobTitle(site.job.title)}
                disbursementMethod={capitalize(site.disbursementMethod)}
                employeeID={site?.employeeId || '--'}
                status={site?.status || '--'}
                dismissReason={site?.dismissReason}
                onEdit={() => setCurrentEditSite(site)}
                showEdit={employeeDetail.status !== EmployeeStatus.DISMISSED && sites?.some((s) => s.id === site.id && s.role === SiteRole.ADMIN)}
                isLoading={isFetchEmployeeLoading}
              />
            ))
          : null}
      </TableBody>
    </Table>
  );

  const orgAdminSiteTable = (
    <Table appearance="noContainer">
      <TableHead>
        <HeaderRow bgColor="transparent">
          <HeaderCell align="left">Site</HeaderCell>
          <HeaderCell align="left">Role</HeaderCell>
          <HeaderCell align="left">Job Title</HeaderCell>
          <HeaderCell align="left">Employee ID</HeaderCell>
          <HeaderCell align="left">Disbursement</HeaderCell>
          <HeaderCell align="right" />
        </HeaderRow>
      </TableHead>

      <TableBody>{employeeDetail && <SiteRowAdmin isLoading={isFetchEmployeeLoading} userType={employeeDetail.type} role={roleSiteDisplay(SiteRole.ADMIN)} site="All" jobTitle="--" employeeID="--" disbursementMethod="Instant" />}</TableBody>
    </Table>
  );

  const cardExpired = false;

  const nameMatches = employeeDetail?.firstName === employeeDetail?.user?.firstName && employeeDetail?.lastName === employeeDetail?.user?.lastName;

  const latestEmailIdentity = (employeeDetail?.user?.identities || []).find(({ verified, type }) => verified && type === 'email')?.value;
  const latestMobileIdentity = (employeeDetail?.user?.identities || []).find(({ verified, type }) => verified && type === 'mobile')?.value;

  const currentEditSiteRole = (() => {
    if (employeeDetail?.type === EmployeeType.ADMIN) return { value: 'admin', label: 'Organization Admin' };
    // we need this because while both site roles and user types are being mashed into one option list,
    // and there's a value conflict with the Site Admins and Org admins both having 'admin' as their value.
    // Therefore, we set the site admin value to 'siteadmin'. When reading it back from the API,
    // we determine whether the employee is a site admin or org admin by the user type rather than the site role
    const siteRoleOptions = [
      { value: 'admin', label: 'Site Admin' },
      { value: EmployeeRole.MANAGER, label: 'Manager' },
      { value: EmployeeRole.EMPLOYEE, label: 'Employee' },
    ];
    return (siteRoleOptions || []).find((siteRole) => siteRole.value === currentEditSite?.role);
  })();

  const currentEditSiteJob = currentEditSite?.job as IJobTitleState;

  const addSiteApiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Employee.ADD_EMPLOYEE_TO_SITE_REQUEST)) as IApiErrorPayloadWithCode;

  const { errors, touched, isValid, dirty, handleBlur, handleSubmit, getFieldProps, resetForm, setFieldValue, values } = useFormik({
    initialValues: {
      site: { label: currentEditSite?.name, value: currentEditSite?.id },
      role: employeeDetail?.type === EmployeeType.USER && currentEditSiteRole?.value === 'admin' ? { label: 'Site Admin', value: EmployeeRole.SITE_ADMIN } : currentEditSiteRole,
      jobTitle: { label: currentEditSiteJob?.title, value: currentEditSiteJob },
      disbursementMethod: DisbursementOptions.find((option) => option.value === currentEditSite?.disbursementMethod) || DisbursementOptions[0],
      employeeId: currentEditSite?.employeeId,
    },
    onSubmit: (values: IEditUserSiteRole) => {
      if (!isSiteRole(values.role?.value)) {
        dispatch(updateOrgEmployeeTypeRequest(employeeDetail.id));
      } else if (
        (values.role && values.role.value !== currentEditSiteRole?.value) ||
        (values.jobTitle && values.jobTitle.value.title !== currentEditSiteJob?.title) ||
        (values.disbursementMethod && values.disbursementMethod.value !== currentEditSite?.disbursementMethod) ||
        values.employeeId !== currentEditSite?.employeeId
      ) {
        updateEmployeeSiteRole({
          role: values.role.value === EmployeeRole.SITE_ADMIN ? EmployeeRole.ADMIN : values.role.value,
          job: { title: values.jobTitle.value.title },
          disbursementMethod: values.disbursementMethod.value,
          employeeId: values.employeeId,
        });
      } else {
        setCurrentEditSite(null);
      }
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const updateEmployeeSiteRole = (payload: IUpdateEmployeeSite) => {
    if (employeeDetail?.id && currentEditSite?.id) {
      dispatch(updateEmployeeSiteRoleRequest(employeeDetail.id, currentEditSite.id, payload, { successMessage: 'Employee site info updated successfully' }));
    }
  };

  const onRemoveSite = () => {
    if (employeeDetail?.id && currentEditSite?.id) {
      dispatch(removeEmployeeFromSiteRequest(employeeDetail.id, currentEditSite.id, { successMessage: 'User removed from site successfully' }));
    }
  };

  const onUnlinkIntegration = (integration: IEmployeeIntegration) => {
    if (employeeDetail?.id && currentEditSite?.id) {
      const onUnlinkIntegrationSuccess = () => {
        setCurrentEditSite(null);
        resetForm();
      };
      dispatch(unlinkEmployeeRequest(employeeDetail, integration, onUnlinkIntegrationSuccess));
    }
  };

  const onDismissSite = (dismissReason: string) => {
    updateEmployeeSiteRole({ dismissReason, status: EmployeeStatus.DISMISSED });
  };

  const onActivateSite = () => {
    if (employeeDetail?.id && currentEditSite?.id) {
      dispatch(updateEmployeeSiteRoleRequest(employeeDetail.id, currentEditSite.id, { status: EmployeeStatus.APPROVED }, { successMessage: 'User activated on site successfully' }));
    }
  };

  const validateField = (field: 'role' | 'jobTitle' | 'employeeId' | 'disbursementMethod') => Boolean(touched[field] && errors[field]);

  const addToSiteValidateField = (field: 'role' | 'jobTitle' | 'employeeId' | 'site' | 'disbursementMethod') => Boolean(AddUserFormik.touched[field] && AddUserFormik.errors[field]);
  const updateEmployeeSiteInfoStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));

  const removeEmployeeFromSiteStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employee.REMOVE_EMPLOYEE_FROM_SITE_REQUEST));

  const showReviewModal = () => setReviewEmployeeModalVisible(true);
  const closeReviewModal = () => setReviewEmployeeModalVisible(false);

  const activateUser = () => {
    dispatch(updateEmployeeRequest(id, { status: EmployeeStatus.APPROVED }, { successMessage: 'Successfully activated employee' }));
  };

  useEffect(() => {
    if (id) {
      dispatch(getEmployeePayoutsRequest(id, { sort: paymentHistorySort, limit: paymentHistoryRecordsPerPage, offset: paymentHistoryOffset, q }));
    }
  }, [id, paymentHistoryOffset, paymentHistoryRecordsPerPage, paymentHistorySort, q]);

  useEffect(() => {
    if (updateEmployeeSiteInfoStatus === RequestStatus.SUCCESS || removeEmployeeFromSiteStatus === RequestStatus.SUCCESS) {
      dispatch(clearRequest(Employee.REMOVE_EMPLOYEE_FROM_SITE_REQUEST));
      dispatch(clearRequest(Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));
      setCurrentEditSite(null);
      resetForm();
      // only update payouts if admin still has permission to edit employee
      if (currentEmployeeType === EmployeeType.ADMIN || sites?.some((site) => employeeDetail?.sites.some((s) => s.id === site.id))) {
        updatePayouts();
      } else {
        history.push(routeConstants.private.employees);
      }
    }
  }, [dispatch, updateEmployeeSiteInfoStatus, removeEmployeeFromSiteStatus]);

  // fetch info for credit card if it exists on file
  const cardId = employeeDetail?.user?.card?.id;
  const currentEmployeeDetails = employeeDetail?.id === id;
  useEffect(() => {
    if (currentEmployeeDetails && cardId) {
      dispatch(getEmployeeCardRequest(id, cardId));
    }
  }, [id, currentEmployeeDetails, cardId, dispatch]);

  const paymentHistoryStatusValues = useMemo(() => paymentHistoryStatusOptions.filter((option) => option.value === (paymentHistoryStatusFilter as PayoutStatus)), [paymentHistoryStatusOptions]);

  const siteOptions = sortedManagedsites && [
    { label: 'All', value: null },
    ...sortedManagedsites.map((site) => ({
      label: site.name,
      value: site.id,
    })),
  ];
  const paymentMethod = (() => {
    const cardDetail = employeeDetail?.user?.cardDetail;
    return isGetEmployeeDetailCardLoading !== false ? '--' : cardDetail ? <PaymentMethodAccountNumber showIcon={false} cardType={cardDetail.network} cardNumber={cardDetail.last4} /> : 'No card on file';
  })();
  const paymentNetwork = employeeDetail?.user?.cardDetail?.network;
  const paymentIcon = getPaymentTypeIcon(paymentNetwork);

  const belongsAllSites = sites && employeeDetail?.sites && sites.every((val) => employeeDetail?.sites.map((s) => s.id).includes(val.id));

  const exportCSV = async (employee: IEmployee, q?: IApiQueryParam[], sort?: string): Promise<IExportCSV> => {
    const response = await API.Employees.listPayouts(employee.id, { q, sort, offset: 0, limit: 0 });
    return CSVFormatter.employeePayouts(employee, response.body?.data);
  };

  return (
    <>
      <ReviewUserScreen employee={employeeDetail} onClose={closeReviewModal} onDeny={closeReviewModal} visible={reviewEmployeeModalVisible} />

      <ActivateUser isActive={reactivateUser} onActivate={activateUser} onClose={() => setReactivateUser(false)} />

      {employeeDetail?.status === EmployeeStatus.DISMISSED && (
        <Banner data-cy="alert-banner">
          <H4>
            User is dismissed from all sites.&nbsp;
            {employeeDetail.user && (
              <ButtonText
                label="Reactivate user"
                onClick={() => {
                  setReactivateUser(!reactivateUser);
                }}
              />
            )}
          </H4>
        </Banner>
      )}

      <PageHeader hasStats>
        <PageHeaderContent stackable>
          <PageHeaderAvatarLayout>
            <PageHeaderAvatar username={fullName} size="large" image={employeeDetail?.user?.avatarUrl} hasPaymentMethod={!!employeeDetail?.user?.card} isLoading={isFetchEmployeeLoading} />

            {isFetchEmployeeLoading ? (
              <Skeleton width={200} />
            ) : (
              <UserNames>
                <H1 marginBottom={0}>{fullName}</H1>
                {employeeDetail?.user && !nameMatches && (
                  <RealName>
                    {`${employeeDetail?.user?.firstName || ''} ${employeeDetail?.user?.lastName || ''}`}
                    <TooltipIcon id="realName">Employee&apos;s spelling of their name.</TooltipIcon>
                  </RealName>
                )}
              </UserNames>
            )}
          </PageHeaderAvatarLayout>

          {employeeDetail && !isFetchEmployeeLoading && hasEditPermission && (
            <PageActions>
              <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
                <PageHeaderAction appearance="tertiary" label="Edit" onClick={() => setEditUser(!editUser)} data-cy="edit-user-button" />
              </PermissionGate>
            </PageActions>
          )}
        </PageHeaderContent>

        <CardRow marginBottom="small">
          {statusDisplay && (
            <Card data-cy="status-card">
              <CardIconStatus isLoading={isFetchEmployeeLoading || isUpdateEmployeeLoading} color={statusDisplay.color} icon={statusDisplay.icon} />
              <CardContent>
                <CardLabel isLoading={isFetchEmployeeLoading}>Status</CardLabel>
                <CardValue isLoading={isFetchEmployeeLoading || isUpdateEmployeeLoading}>
                  {statusDisplay.text}
                  {employeeDetail?.status === EmployeeStatus.DISMISSED && employeeDetail.dismissReason && (
                    <TooltipIcon id={`tooltip-dismissed-${employeeDetail.id}`}>
                      <p>
                        <strong>Dismiss Reason</strong>
                        <br />
                        {employeeDetail.dismissReason}
                      </p>
                    </TooltipIcon>
                  )}
                </CardValue>

                {statusDisplay.action && !statusDisplay.hideAction && (
                  <CardAction
                    label={statusDisplay.actionText}
                    onClick={statusDisplay.action}
                    disabled={statusDisplay.disabled || isUpdateEmployeeLoading}
                    isLoading={isFetchEmployeeLoading || isUpdateEmployeeLoading}
                    tooltip={
                      statusDisplay.disabled ? (
                        <>
                          You do not have permission to review users.
                          <br />
                          Contact your supervisor for more information.
                        </>
                      ) : undefined
                    }
                  />
                )}
              </CardContent>
            </Card>
          )}

          <Card data-cy="expired-payments-card">
            <CardIconStatus color={Appearance.DANGER} icon="Payment" />
            <CardContent>
              <CardLabel isLoading={isFetchEmployeeLoading || isPayoutStatsLoading}>Expired Payments ({expired?.count || '0'})</CardLabel>
              <CardValue isLoading={isFetchEmployeeLoading || isPayoutStatsLoading}>{centsToDollarsDisplay(expired?.gross || 0)}</CardValue>
            </CardContent>
          </Card>

          <Card data-cy="pending-payments-card">
            <CardIconStatus color={Appearance.WARNING} icon="Payment" />
            <CardContent>
              <CardLabel isLoading={isFetchEmployeeLoading || isPayoutStatsLoading}>Pending Payments ({pending?.count || '0'})</CardLabel>
              <CardValue isLoading={isFetchEmployeeLoading || isPayoutStatsLoading}>{centsToDollarsDisplay(pending?.gross || 0)}</CardValue>
            </CardContent>
          </Card>

          <Card data-cy="payment-method-card">
            {!cardExpired && !!paymentMethod ? <CardIconStatus isLoading={isFetchEmployeeLoading} icon={paymentIcon} color={Appearance.NEUTRAL} /> : <CardIcon icon="Payment" />}
            <CardContent>
              <CardLabel isLoading={isFetchEmployeeLoading}>Payment Method</CardLabel>
              <CardValue isLoading={isFetchEmployeeLoading}>{paymentMethod}</CardValue>
              {cardExpired && <CardDetail>Expired</CardDetail>}
            </CardContent>
          </Card>
        </CardRow>
      </PageHeader>

      {employeeDetail?.user && !isFetchEmployeeLoading && (
        <AccordionSection afterHeaderCardRow title="Personal Info" dataCy="info-section">
          <PersonalInfoLayout>
            <FieldWrapper label="Mobile">
              <Input value={latestMobileIdentity ? formatMobile(latestMobileIdentity) : '--'} readOnly />
            </FieldWrapper>

            <FieldWrapper label="Email">
              <Input value={latestEmailIdentity || '--'} readOnly />
            </FieldWrapper>

            <FieldWrapper label="Date Added">
              <Input value={employeeDetail?.createdAt ? formatDateForUI(employeeDetail?.createdAt) : '--'} readOnly />
            </FieldWrapper>
          </PersonalInfoLayout>
        </AccordionSection>
      )}
      <AccordionSection
        title="Sites"
        afterHeaderCardRow
        hideContentOverflow
        isLoading={isFetchEmployeeLoading}
        actions={
          <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
            {employeeDetail?.status !== EmployeeStatus.DISMISSED && (
              <TooltipWrapper data-cy="tooltip-wrapper">
                <Button appearance="primary" size="small" label="Add Site" disabled={belongsAllSites} onClick={() => setAddSite(true)} data-cy="add-site-button" />
                {belongsAllSites && (
                  <TooltipIcon id="tooltip-all-sites" positionStrategy="fixed">
                    User already belongs to all sites
                  </TooltipIcon>
                )}
              </TooltipWrapper>
            )}
          </PermissionGate>
        }
      >
        {employeeDetail &&
          (!isEmpty(employeeDetail.sites) ? (
            <TableContainer>{employeeDetail.type === EmployeeType.ADMIN ? orgAdminSiteTable : nonAdminSiteTable}</TableContainer>
          ) : (
            <>
              <ShorterEmptyContainer>
                <Empty icon="Site" message="No sites found." />
              </ShorterEmptyContainer>

              {employeeDetail.status !== EmployeeStatus.DISMISSED && (
                <StyledSection>
                  <CenteredButtonAdd width={150} onClick={() => setAddSite(true)}>
                    Add user to a site
                  </CenteredButtonAdd>
                </StyledSection>
              )}
            </>
          ))}
      </AccordionSection>

      <AccordionSection
        dataCy="payment-history-accordion"
        title="Payment History"
        isLoading={isPayoutsLoading}
        hideContentOverflow
        actions={
          <>
            <DateRangePicker
              fluidWidth={false}
              inputHeight="small"
              onSubmit={() => {
                setPaymentHistoryOffset(defaultOffset);
              }}
              pickerPosition="right"
              startDate={currentDateRange.startDate}
              endDate={currentDateRange.endDate}
              submitLabel="apply"
            />
            <FilterButton filterActive={paymentFilter} appliedFilters={qWithoutDates.length} onClick={() => setPaymentFilters(!paymentFilter)} />
            {employeeDetail && (
              <ButtonExportCSVAsync
                data-cy="export-btn"
                disabled={!payouts || payouts.length === 0}
                exportFunc={() => exportCSV(employeeDetail, q, paymentHistorySort)}
                fileName={`Payments_${fullName}_${currentDateRange.startDate}_${currentDateRange.endDate}`}
              />
            )}
          </>
        }
        filters={
          <FilterNested isVisible={paymentFilter}>
            <FieldWrapper label="Site">
              {siteOptions && (
                <Select
                  data-cy="site-dropdown"
                  placeholder="All"
                  multi={false}
                  options={siteOptions}
                  values={siteOptions && paymentHistorySiteFilter ? siteOptions.filter((so) => (so.value ? paymentHistorySiteFilter.includes(so.value) : [])) : []}
                  onChange={(selectedOptions) => {
                    if (selectedOptions?.[0]) {
                      setPaymentHistorySiteFilter(selectedOptions[0].value);
                      setPaymentHistoryOffset(defaultOffset);
                    }
                  }}
                />
              )}
            </FieldWrapper>
            <FieldWrapper label="Payment Status">
              <Select
                data-cy="payment-status-dropdown"
                placeholder="All"
                multi={false}
                onChange={(choice: Array<{ label: string; value: string }>) => {
                  if (choice?.[0]) setPaymentHistoryStatusFilter(choice?.[0].value);
                  setPaymentHistoryOffset(defaultOffset);
                }}
                options={paymentHistoryStatusOptions}
                values={paymentHistoryStatusValues}
              />
            </FieldWrapper>
          </FilterNested>
        }
      >
        <UserDetailPayouts
          payouts={payouts}
          recordsPerPage={paymentHistoryRecordsPerPage}
          setRecordsPerPage={setPaymentHistoryRecordsPerPage}
          offset={paymentHistoryOffset}
          setOffset={setPaymentHistoryOffset}
          sort={paymentHistorySort}
          setSort={setPaymentHistorySort}
          totalRecords={payoutsTotal}
          isLoading={isPayoutsLoading}
          onUpdate={getEmployeePayoutStats}
          appearance="noContainer"
        />
        {payouts && payouts.length === 0 && (
          <EmptyContainer>
            <Empty icon="Payment" message="No payments found." />
          </EmptyContainer>
        )}
      </AccordionSection>

      {employeeDetail && (
        <>
          {addSite && (
            <AddUserToSite
              apiErrors={addSiteApiErrors}
              avatarUrl={employeeDetail.user?.avatarUrl}
              dirty={AddUserFormik.dirty}
              errors={AddUserFormik.errors}
              fullName={`${employeeDetail.firstName} ${employeeDetail.lastName}`}
              getFieldProps={AddUserFormik.getFieldProps}
              handleBlur={AddUserFormik.handleBlur}
              hasPaymentMethod={!!employeeDetail?.user?.card}
              invitationStatus={employeeDetail.status as EmployeeStatus}
              isActive={addSite}
              isLoading={isAddEmployeeToSiteLoading || isUpdateEmployeeLoading}
              isSiteRole={isSiteRole(AddUserFormik.values.role.value as EmployeeRole)}
              isValid={AddUserFormik.isValid}
              onClose={() => {
                AddUserFormik.resetForm();
                dispatch(clearRequest(Employee.ADD_EMPLOYEE_TO_SITE_REQUEST));
                setAddSite(false);
              }}
              onSubmit={AddUserFormik.handleSubmit}
              setFieldTouched={AddUserFormik.setFieldTouched}
              setFieldValue={AddUserFormik.setFieldValue}
              showReviewModal={() => {
                setAddSite(false);
                showReviewModal();
              }}
              sites={sites && getAvailableEmployerSites(sites, employeeDetail)}
              validateField={addToSiteValidateField}
              values={AddUserFormik.values}
            />
          )}

          {editUser && (
            <EditUser
              isActive={editUser}
              onClose={() => setEditUser(false)}
              showReviewModal={() => {
                setEditUser(false);
                showReviewModal();
              }}
            />
          )}

          {currentEditSite && (
            <EditSite
              currentUser={currentUser}
              dirty={dirty}
              employeeDetail={employeeDetail}
              employeeSiteInfo={{
                role: values.role,
                job: values.jobTitle?.value,
                disbursementMethod: values.disbursementMethod,
                employeeId: values.employeeId,
                siteName: currentEditSite.name,
                status: currentEditSite.status,
              }}
              errors={errors}
              getFieldProps={getFieldProps}
              handleBlur={handleBlur}
              isActive={!!currentEditSite}
              isLoading={isUpdateSiteRoleLoading || isUpdateEmployeeLoading}
              isRemoveLoading={isRemoveFromSiteLoading}
              isSiteRole={!!(values?.role?.value && isSiteRole(values.role.value as EmployeeRole))}
              isValid={isValid}
              onActivateSite={onActivateSite}
              onDismissSite={onDismissSite}
              onClose={() => {
                resetForm();
                setCurrentEditSite(null);
              }}
              onRemoveSite={onRemoveSite}
              onUnlinkIntegration={onUnlinkIntegration}
              isUnlinkIntegrationLoading={isUnlinkingEmployeeLoading}
              onSubmit={handleSubmit}
              setFieldValue={setFieldValue}
              showReviewModal={() => {
                setCurrentEditSite(null);
                showReviewModal();
              }}
              site={currentEditSite}
              validateField={validateField}
            />
          )}
        </>
      )}
    </>
  );
};

export default UserDetail;
